/* Social Independence landing page interactions.
   - Press: auto-advancing logo carousel; the active outlet's logo slides to
     the centre line and its quote cross-fades in.
   - Twirly Cord Club: native scroll-snap gallery + click-drag for mouse users.
   Self-contained; no framework dependencies. */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------- Press ---------------------------
     Infinite, looping logo carousel. The active outlet's logo is centred on
     the viewport's mid-line and its quote cross-fades in. To loop seamlessly
     in both directions, the original logo set is flanked by a full clone on
     each side ([clones][originals][clones]); we keep the "live" position in
     the middle copy and silently snap back by one copy-width whenever a move
     lands in a clone region, so the strip reads as endless and the last item
     wraps around to sit left of the first. Centring is computed from each
     item's measured offset (not an accumulated delta), which is deterministic
     and avoids the first-paint drift where the track started scrolled to the
     end. */
  var TRANSITION_MS = 600; // keep in sync with .slp-press__track transition

  function initPress(root) {
    // social-lp.js is included by more than one section (press + twirly), so
    // the script can execute more than once. Guard so we only build the
    // carousel (and its clones) a single time per element.
    if (root.dataset.slpPressReady) return;
    root.dataset.slpPressReady = '1';

    var track = root.querySelector('.slp-press__track');
    var viewport = root.querySelector('.slp-press__viewport');
    var quotes = Array.prototype.slice.call(
      root.querySelectorAll('.slp-press__quote')
    );
    var dots = Array.prototype.slice.call(
      root.querySelectorAll('.slp-press__dot')
    );
    var prevArrow = root.querySelector('.slp-press__arrow--prev');
    var nextArrow = root.querySelector('.slp-press__arrow--next');
    var realLogos = Array.prototype.slice.call(
      track.querySelectorAll('.slp-press__logo')
    );
    var n = realLogos.length;
    if (!track || n === 0) return;

    var interval = parseInt(root.dataset.interval, 10) || 4000;

    // Single outlet: nothing to scroll. Just mark it active.
    if (n < 2) {
      realLogos[0].classList.add('is-active');
      if (quotes[0]) quotes[0].classList.add('is-active');
      if (dots[0]) dots[0].classList.add('is-active');
      return;
    }

    // Build the flanking clones so there is always content on both sides.
    function cloneSet() {
      var frag = document.createDocumentFragment();
      realLogos.forEach(function (logo) {
        var clone = logo.cloneNode(true);
        clone.classList.add('is-clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.tabIndex = -1;
        frag.appendChild(clone);
      });
      return frag;
    }
    track.appendChild(cloneSet()); // right copy
    track.insertBefore(cloneSet(), track.firstChild); // left copy
    var items = Array.prototype.slice.call(
      track.querySelectorAll('.slp-press__logo')
    ); // length 3n, middle copy starts at index n

    var pos = n; // index into `items`; start on the first real logo
    var timer = null;

    function center(p, animate) {
      var el = items[p];
      if (!animate) track.style.transition = 'none';
      // Track left edge sits at the viewport's left edge (margin:auto collapses
      // because the track is always wider than the viewport), so an item's
      // untransformed centre in viewport space is just its offset within the
      // track. Translate so that centre lands on the viewport mid-line.
      var tx = viewport.clientWidth / 2 - (el.offsetLeft + el.offsetWidth / 2);
      track.style.transform = 'translateX(' + tx + 'px)';
      if (!animate) {
        void track.offsetWidth; // flush, so the next change animates
        track.style.transition = '';
      }
    }

    function updateActive(realIndex) {
      items.forEach(function (el, i) {
        el.classList.toggle('is-active', i === pos);
      });
      quotes.forEach(function (el, i) {
        el.classList.toggle('is-active', i === realIndex);
      });
      dots.forEach(function (el, i) {
        el.classList.toggle('is-active', i === realIndex);
        el.setAttribute('aria-selected', i === realIndex ? 'true' : 'false');
      });
    }

    var normalizeTimer = null;
    // After a move settles, snap `pos` back into the middle copy. The target
    // shows the identical logo with identical neighbours, so the snap (done
    // without animation) is invisible — this is what makes the loop endless.
    function normalize() {
      var base = (((pos - n) % n) + n) % n;
      var middle = n + base;
      if (middle !== pos) {
        pos = middle;
        center(pos, false);
        items.forEach(function (el, i) {
          el.classList.toggle('is-active', i === pos);
        });
      }
    }

    function go(targetPos, animate) {
      pos = targetPos;
      var realIndex = (((pos % n) + n) % n);
      updateActive(realIndex);
      center(pos, animate !== false && !reduceMotion);
      window.clearTimeout(normalizeTimer);
      if (animate !== false && !reduceMotion) {
        normalizeTimer = window.setTimeout(normalize, TRANSITION_MS + 40);
      } else {
        normalize();
      }
    }

    function next() {
      go(pos + 1);
    }
    function prev() {
      go(pos - 1);
    }
    // Jump to a specific outlet via its nearest on-strip occurrence, so the
    // animation takes the shortest path and the loop stays seamless.
    function jumpTo(realIndex) {
      var candidates = [realIndex, realIndex + n, realIndex + 2 * n];
      var best = candidates[0];
      candidates.forEach(function (c) {
        if (Math.abs(c - pos) < Math.abs(best - pos)) best = c;
      });
      go(best);
    }

    function start() {
      if (reduceMotion) return;
      stop();
      timer = window.setInterval(next, interval);
    }
    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        jumpTo(i);
        start();
      });
    });
    items.forEach(function (logo) {
      logo.addEventListener('click', function () {
        jumpTo(parseInt(logo.dataset.index, 10) || 0);
        start();
      });
    });
    if (prevArrow) {
      prevArrow.addEventListener('click', function () {
        prev();
        start();
      });
    }
    if (nextArrow) {
      nextArrow.addEventListener('click', function () {
        next();
        start();
      });
    }

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);

    var resizeRAF;
    window.addEventListener('resize', function () {
      window.cancelAnimationFrame(resizeRAF);
      resizeRAF = window.requestAnimationFrame(function () {
        center(pos, false);
      });
    });

    // Initial paint, then re-centre once images have real dimensions (the
    // measurement is meaningless before the logos load, which used to leave
    // the track parked at the far end on first paint).
    go(pos, false);
    var imgs = Array.prototype.slice.call(track.querySelectorAll('img'));
    Promise.all(
      imgs.map(function (img) {
        if (img.complete) return null;
        return new Promise(function (resolve) {
          img.addEventListener('load', resolve);
          img.addEventListener('error', resolve);
        });
      })
    ).then(function () {
      center(pos, false);
      start();
    });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        center(pos, false);
      });
    }
  }

  /* ------------------------ Twirly gallery ---------------------- */
  function initDragScroll(el) {
    if (el.dataset.slpDragReady) return; // guard against double init (see initPress)
    el.dataset.slpDragReady = '1';

    var isDown = false;
    var startX = 0;
    var startScroll = 0;
    var moved = false;

    el.addEventListener('pointerdown', function (e) {
      // Only hijack precise pointers (mouse); let touch use native scroll.
      if (e.pointerType === 'touch') return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      el.classList.add('is-dragging');
    });
    el.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      el.scrollLeft = startScroll - dx;
    });
    function end() {
      isDown = false;
      el.classList.remove('is-dragging');
    }
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('pointerleave', end);
    // Prevent the click-through after a drag from following a link.
    el.addEventListener(
      'click',
      function (e) {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  function init() {
    document.querySelectorAll('.slp-press').forEach(initPress);
    document.querySelectorAll('.slp-twirly__gallery').forEach(initDragScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
